import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import { AuthImpersonationService, IMPERSONATION_HEADER_USER_ID } from '../providers';
import { NativeAuthService } from '../providers/native-auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly keycloak = inject(Keycloak, { optional: true });

  constructor(
    private readonly authImporsinatorService: AuthImpersonationService,
    private readonly nativeAuthService: NativeAuthService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.endsWith('token') || req.url.endsWith('logout')) {
      return next.handle(req);
    }

    return this.getAccessToken().pipe(
      switchMap(token => {
        const requestHeaders: Record<string, string> = {};

        if (token) {
          requestHeaders['Authorization'] = `Bearer ${token}`;
        }

        if (this.authImporsinatorService.isImpersonating()) {
          const impersonatedUser = this.authImporsinatorService.getStateValue().impersonatedUser;
          requestHeaders[IMPERSONATION_HEADER_USER_ID] = impersonatedUser as string;
        }

        return next.handle(req.clone({ setHeaders: requestHeaders }));
      })
    );
  }

  private getAccessToken(): Observable<string | undefined> {
    if (!this.keycloak) {
      return from(
        this.nativeAuthService.ensureValidToken().then(() => this.nativeAuthService.getAccessToken())
      );
    }

    const keycloak = this.keycloak;
    return from(
      keycloak.updateToken(30).catch(() => false).then(() => keycloak.token)
    );
  }
}
